<template>
  <div class="invite-members-step">
    <div class="step-content">
      <h1 class="step-title">Invite Members</h1>
      <p class="step-subtitle">
        Add people so they can join discussions and activities in your community.
      </p>

      <!-- Invite and share section -->
      <h2 class="section-title mt-8">Invite and share</h2>

      <!-- Email invite -->
      <div class="form-field mt-6">
        <label class="field-label">Invite by email</label>

        <div class="email-input-row">
          <div class="email-input-wrapper">
            <v-text-field
              v-model="emailInput"
              :placeholder="emailList.length > 0 ? '' : 'Type email and press Enter, comma or space'"
              outlined
              dense
              hide-details
              class="email-field"
              prepend-icon=""
              prepend-inner-icon=""
              append-icon=""
              append-outer-icon=""
              @keyup.enter="addEmail"
              @input="handleEmailInput"
            >
              <template v-slot:prepend-inner>
                <div v-if="emailList.length > 0" class="email-chips-inline">
                  <v-chip
                    v-for="(email, index) in emailList"
                    :key="index"
                    x-small
                    class="email-chip-inline"
                  >
                    {{ email }}
                    <v-icon
                      size="10"
                      class="ml-1 close-icon"
                      @click="removeEmail(index)"
                    >
                      fa-solid fa-times
                    </v-icon>
                  </v-chip>
                </div>
              </template>
            </v-text-field>
          </div>
          <v-btn
            class="send-invite-btn"
            height="40"
            :disabled="emailList.length === 0"
            @click="sendInvites"
          >
            Send invite
          </v-btn>
        </div>
      </div>

      <!-- Sent invitations display -->
      <div v-if="sentEmails.length > 0" class="form-field mt-6">
        <div class="sent-invitation-wrapper">
          <label class="sent-invitation-label">Invitation sent to:</label>
          <div class="sent-emails-display">
            <v-icon size="16" color="darkGreen" class="mr-2">fa-solid fa-user</v-icon>
            <div class="sent-email-text">{{ sentEmails[0] }}</div>
            <div class="and-text" v-if="sentEmails.length > 1">and</div>
            <v-tooltip v-if="sentEmails.length > 1" bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="sent-count-chip" v-bind="attrs" v-on="on">
                  {{ sentEmails.length - 1 }}+
                </div>
              </template>
              <div class="tooltip-emails">
                <div v-for="(email, index) in sentEmails.slice(1)" :key="index" class="tooltip-email">
                  {{ email }}
                </div>
              </div>
            </v-tooltip>
          </div>
        </div>
      </div>

      <!-- Social invite -->
      <div class="form-field mt-6">
        <div class="social-row">
          <label class="field-label">Invite by social:</label>
          <div class="social-buttons">
            <v-btn
              icon
              class="social-icon"
              @click="copyLink"
            >
              <v-icon size="14" color="darkGreen">fa-regular fa-link</v-icon>
            </v-btn>
            <v-btn
              icon
              class="social-icon"
              @click="shareOnTelegram"
            >
              <v-icon size="14" color="darkGreen">fa-brands fa-telegram</v-icon>
            </v-btn>
            <v-btn
              icon
              class="social-icon"
              @click="shareOnWhatsApp"
            >
              <v-icon size="14" color="darkGreen">fa-brands fa-whatsapp</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-divider class="my-8" />

      <!-- Import contacts section -->
      <h2 class="section-title">Import contacts</h2>

      <div class="import-row mt-3">
        <p class="section-description">
          Upload a CSV / XLSX file with a emails column defined
        </p>

        <input
          ref="fileInput"
          type="file"
          accept=".csv,.xlsx"
          style="display: none"
          @change="handleFileUpload"
        />

        <v-btn
          rounded
          class="upload-btn"
          height="36"
          @click="$refs.fileInput.click()"
        >
          Upload file
          <v-icon right size="18">fa-regular fa-upload</v-icon>
        </v-btn>
      </div>

      <div v-if="csvFile" class="file-info mt-2">
        <v-icon size="16" color="success" class="mr-1">fa-solid fa-check-circle</v-icon>
        <span class="file-name">{{ csvFile.name }}</span>
        <v-btn icon x-small class="ml-2" @click="clearFile">
          <v-icon size="12">fa-solid fa-times</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Action buttons - right justified -->
    <div class="step-actions">
      <v-btn
        large
        rounded
        class="continue-btn"
        min-width="180"
        height="36"
        :disabled="!canSubmit"
        @click="submitInvites"
      >
        Continue
        <v-icon right size="18">fa-regular fa-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Loading overlay -->
    <loading-overlay v-if="loading" message="Sending invitations..." />

    <!-- Success snackbar -->
    <v-snackbar v-model="snackbar" :timeout="3000" color="success">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import clubService from '../../services/club.service'
import LoadingOverlay from '../LoadingOverlay.vue'

export default {
  name: 'InviteMembersModal',
  components: {
    LoadingOverlay
  },
  props: {
    communityId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      emailInput: '',
      emailList: [],
      sentEmails: [],
      csvFile: null,
      loading: false,
      snackbar: false,
      snackbarMessage: '',
      inviteCode: null,
      loadingInviteCode: false
    }
  },
  mounted() {
    // Fetch the invite code when the modal opens
    this.fetchInviteCode()
  },
  computed: {
    canSubmit() {
      // Can always continue (invitations are optional)
      return true
    }
  },
  methods: {
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },

    handleEmailInput(value) {
      // Check if the input contains comma or space
      if (value.includes(',') || value.includes(' ')) {
        // Split by comma or space and process each email
        const emails = value.split(/[,\s]+/).filter(e => e.trim())

        // Clear the input first
        this.emailInput = ''

        // Add each valid email
        emails.forEach(email => {
          email = email.trim()
          if (email && this.validateEmail(email) && !this.emailList.includes(email)) {
            this.emailList.push(email)
          } else if (email && !this.validateEmail(email)) {
            this.snackbarMessage = `Invalid email: ${email}`
            this.snackbar = true
          }
        })
      }
    },

    addEmail() {
      const email = this.emailInput.trim()

      if (!email) {
        return
      }

      if (!this.validateEmail(email)) {
        this.snackbarMessage = 'Please enter a valid email address'
        this.snackbar = true
        return
      }

      if (this.emailList.includes(email)) {
        this.snackbarMessage = 'Email already added'
        this.snackbar = true
        return
      }

      this.emailList.push(email)
      this.emailInput = ''
    },

    removeEmail(index) {
      this.emailList.splice(index, 1)
    },

    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.csvFile = file
        this.processFile(file)
      }
    },

    processFile(file) {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary' })

          // Get the first sheet
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          // Convert to JSON (array of arrays)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: false
          })

          // Extract emails from first column (assuming emails are in first column)
          const extractedEmails = []
          jsonData.forEach((row, index) => {
            // Skip header row if exists
            if (index === 0 && row[0] && (
              row[0].toLowerCase().includes('email') ||
              row[0].toLowerCase().includes('e-mail')
            )) {
              return
            }

            const email = row[0]
            if (email && this.validateEmail(email.trim())) {
              const trimmedEmail = email.trim()
              if (!this.emailList.includes(trimmedEmail) && !this.sentEmails.includes(trimmedEmail)) {
                extractedEmails.push(trimmedEmail)
              }
            }
          })

          if (extractedEmails.length > 0) {
            // Add extracted emails to the email list
            this.emailList = [...this.emailList, ...extractedEmails]
            this.snackbarMessage = `${extractedEmails.length} email(s) extracted from file`
            this.snackbar = true
          } else {
            this.snackbarMessage = 'No valid emails found in file'
            this.snackbar = true
          }
        } catch (error) {
          console.error('Error processing file:', error)
          this.snackbarMessage = 'Error processing file. Please check the format.'
          this.snackbar = true
        }
      }

      reader.readAsBinaryString(file)
    },

    async sendInvites() {
      if (this.emailList.length === 0) {
        return
      }

      this.loading = true

      try {
        // Send invitations via API
        const sentCount = await clubService.inviteMembers(this.communityId, {
          emails: this.emailList
        })

        // Move successfully sent emails to sentEmails
        this.sentEmails = [...this.sentEmails, ...this.emailList]

        // Show success message
        this.snackbarMessage = `${sentCount} invitation(s) sent successfully!`
        this.snackbar = true

        // Clear the email list and input
        this.emailList = []
        this.emailInput = ''
      } catch (error) {
        console.error('Error sending invites:', error)
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to send invitations'
        this.snackbarMessage = errorMessage
        this.snackbar = true
      } finally {
        this.loading = false
      }
    },

    async fetchInviteCode() {
      if (!this.communityId) {
        console.error('No community ID provided')
        return
      }

      this.loadingInviteCode = true
      try {
        this.inviteCode = await clubService.getSocialInviteCode(this.communityId)
        console.log('Invite code fetched:', this.inviteCode)
      } catch (error) {
        console.error('Failed to fetch invite code:', error)
        this.snackbarMessage = 'Failed to generate invite link. Please try again.'
        this.snackbar = true
      } finally {
        this.loadingInviteCode = false
      }
    },

    getInviteLink() {
      // Traders app URL is always app.swiset.com (production)
      // or localhost for development
      const isDevelopment = process.env.NODE_ENV === 'development' ||
                           window.location.hostname === 'localhost'

      const tradersAppUrl = isDevelopment
        ? 'http://localhost:8080'  // Local traders app
        : 'https://app.swiset.com' // Production traders app

      if (!this.inviteCode) {
        return tradersAppUrl // Login is at root path
      }

      // Traders app login is at root path "/"
      return `${tradersAppUrl}/?inviteCode=${this.inviteCode}`
    },

    copyLink() {
      const link = this.getInviteLink()
      navigator.clipboard.writeText(link)
      this.snackbarMessage = 'Invite link copied to clipboard!'
      this.snackbar = true
    },

    shareOnTelegram() {
      const link = this.getInviteLink()
      const text = encodeURIComponent(`Join our trading community on Swiset!`)
      window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${text}`, '_blank')
    },

    shareOnWhatsApp() {
      const link = this.getInviteLink()
      const text = encodeURIComponent(`Join our trading community on Swiset! ${link}`)
      window.open(`https://wa.me/?text=${text}`, '_blank')
    },

    clearFile() {
      this.csvFile = null
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    submitInvites() {
      // If there are pending emails in the input, remind user to send them
      if (this.emailList.length > 0) {
        this.snackbarMessage = 'You have unsent emails. Click "Send invite" or clear them to continue.'
        this.snackbar = true
        return
      }

      // Emit event to continue (invitations have already been sent)
      this.$emit('invites-sent', this.sentEmails.length)
    },

    goBack() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
.invite-members-step {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: #14161B;
  margin-bottom: 12px;
}

.step-subtitle {
  font-size: 1rem;
  line-height: 1.5;
  color: #6B6B6B;
  margin-bottom: 0;
}

.broker-app .invite-members-step .section-title,
#broker-app .invite-members-step .section-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #14161B !important;
  display: flex;
  align-items: center;
  line-height: 1.2 !important;
  font-family: "Aeonik", sans-serif !important;
}

.section-description {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #6B6B6B;
  margin-bottom: 0;
}

.import-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .section-description {
    flex: 1;
  }
}

.form-field {
  margin-bottom: 0;
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #14161B;
  margin-bottom: 8px;
}

.social-row {
  display: flex;
  align-items: center;
  gap: 16px;

  .field-label {
    margin-bottom: 0;
    flex-shrink: 0;
  }
}

.email-input-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.email-input-wrapper {
  flex: 1;
}

.email-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      min-height: 40px;
      padding: 4px 12px;
    }
  }

  ::v-deep .v-input__prepend-inner {
    margin-top: 0 !important;
    padding-top: 0 !important;
    align-self: center;
  }

  // Remove the envelope icon pseudo-element added by global styles
  ::v-deep .v-text-field__slot::before {
    content: none !important;
    display: none !important;
  }
}

.email-chips-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  max-width: 100%;
}

.email-chip-inline {
  height: 24px !important;
  font-size: 0.75rem;
  padding: 0 8px !important;
  background-color: #E8E8E8 !important;
  color: #14161B !important;
  border-radius: 4px;

  ::v-deep .v-chip__content {
    padding: 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .close-icon {
    cursor: pointer;
    color: #6B6B6B !important;
    transition: color 0.2s;

    &:hover {
      color: #14161B !important;
    }
  }
}

.send-invite-btn {
  background-color: #B4F4D7 !important;
  color: #00875A !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  flex-shrink: 0;

  &:not(.v-btn--disabled):hover {
    background-color: #9FE8C6 !important;
  }

  &.v-btn--disabled {
    background-color: #E8E8E8 !important;
    color: #9E9E9E !important;
  }
}

.sent-invitation-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.v-application .sent-invitation-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #00875A !important;
  margin-bottom: 0;
  flex-shrink: 0;
}

.sent-emails-display {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #00875A;
  flex-wrap: wrap;
}

.sent-emails-display .sent-email-text,
.sent-emails-display .and-text {
  color: #00875A;
  font-size: 0.875rem;
  font-weight: 500;
}

.sent-emails-display .and-text {
  margin: 0 2px;
}

.sent-count-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  background-color: #B4F4D7;
  border-radius: 12px;
  font-size: 0.75rem;
  color: #00875A;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #9FE8C6;
  }
}

.tooltip-emails {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tooltip-email {
  padding: 4px 0;
  font-size: 0.875rem;
  color: white;
}

.social-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
}

.v-application .v-btn.social-icon {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  background-color: transparent !important;
  border: 1px solid #005931 !important;
  border-radius: 50% !important;
  padding: 0 !important;

  &:hover {
    background-color: rgba(0, 89, 49, 0.2) !important;
  }

  ::v-deep .v-icon {
    font-size: 14px !important;
  }
}

.v-btn.upload-btn {
  background-color: #EBEBEB !important;
  color: #14161B !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  min-width: 140px;
  flex-shrink: 0;

  &:hover {
    background-color: #D9D9D9 !important;
  }

  &:not(.v-btn--disabled) {
    background-color: #EBEBEB !important;
    color: #14161B !important;
  }

  ::v-deep .v-icon {
    color: #14161B;
  }
}

.file-name {
  font-size: 0.875rem;
  color: #6B6B6B;
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
}

.continue-btn {
  background-color: #B4F4D7 !important;
  color: #00875A !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;

  &:not(.v-btn--disabled):hover {
    background-color: #9FE8C6 !important;
  }

  &.v-btn--disabled {
    background-color: #E8E8E8 !important;
    color: #9E9E9E !important;
  }
}

@media (max-width: 960px) {
  .email-input-row {
    flex-direction: column;
    align-items: stretch;
  }

  .send-invite-btn {
    width: 100%;
  }
}

.file-info {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #F0F9FF;
  border-radius: 4px;
  border: 1px solid #B4E0FF;

  .file-name {
    font-size: 0.875rem;
    color: #14161B;
    flex: 1;
  }
}
</style>
